/**
 * Session storage helpers, side-effect-free on import (no module-level code).
 *
 * Lives apart from App.js so the boot-loop breaker and load-failure panels in
 * index.js can clear the session without evaluating App's module graph — App's
 * analytics constructor starts a session (network) and utils/csrf.js prefetches
 * a token (network) at import time. Importing THIS module does neither.
 */

// All localStorage keys owned by this app
export const APP_STORAGE_KEYS = [
  'token',
  'impersonation_token',
  'impersonation_tenant_name',
  'impersonation_tenant_id',
  'tenant_slug',
];

export function clearSession() {
  APP_STORAGE_KEYS.forEach(k => localStorage.removeItem(k));
}

const VERSION_KEY = 'ww_version';

// Strict x.y.z with missing components counting as 0 ("2.10" === "2.10.0").
// Anything else (non-numeric, extra components, empty, non-string) is garbage:
// treated as "older" so junk storage keeps today's clear-and-move-forward
// behavior. `current` is assumed valid.
function parseVersion(v) {
  if (typeof v !== 'string') return null;
  const m = v.trim().match(/^(\d+)(?:\.(\d+))?(?:\.(\d+))?$/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2] ?? 0), Number(m[3] ?? 0)];
}

export function isOlderVersion(stored, current) {
  const s = parseVersion(stored);
  if (s === null) return true;
  const c = parseVersion(current);
  for (let i = 0; i < 3; i++) {
    if (s[i] !== c[i]) return s[i] < c[i];
  }
  return false;
}

// Both wrappers never throw: private browsing can deny storage access with a
// DOMException ("Storage is disabled"), and nothing here is worth crashing over.
// readStoredVersion distinguishes "key absent" (normal, value: null) from
// "storage threw" (ok: false) so callers can tell a first visit apart from
// private mode — they must NOT clear or rewrite anything when ok is false.
export function readStoredVersion() {
  try {
    return { ok: true, value: localStorage.getItem(VERSION_KEY) };
  } catch (error) {
    return { ok: false, value: null };
  }
}

export function writeStoredVersion(v) {
  try {
    localStorage.setItem(VERSION_KEY, v);
  } catch (error) {
    // no-op: storage denied (private mode)
  }
}
